/* =========================================================================
   Size Drawer — apre dal trigger <button data-size-drawer-trigger>,
   popola la lista leggendo il <select data-size-trigger-source>, gestisce
   selezione e push effect sul product drawer.
   ========================================================================= */
(function () {
  'use strict';

  if (customElements.get('cz-size-drawer')) return;

  class CzSizeDrawer extends HTMLElement {
    constructor() {
      super();
      this.dialog = null;
      this.listEl = null;
      this.currentSelect = null;
      this.currentTrigger = null;

      this._onDocClick = this._onDocClick.bind(this);
      this._onDialogClick = this._onDialogClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onProductInfoLoaded = this._onProductInfoLoaded.bind(this);
      this._onListClick = this._onListClick.bind(this);
      this._onListKeydown = this._onListKeydown.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-size-drawer-dialog]');
      this.listEl = this.querySelector('[data-size-drawer-list]');
      if (!this.dialog || !this.listEl) return;

      document.addEventListener('click', this._onDocClick);
      this.dialog.addEventListener('click', this._onDialogClick);
      this.dialog.addEventListener('close', this._onDialogClose);
      this.listEl.addEventListener('click', this._onListClick);
      this.listEl.addEventListener('keydown', this._onListKeydown);

      // Initial sync + re-sync after every product-info AJAX swap
      this._syncAllTriggerLabels();
      document.addEventListener('product-info:loaded', this._onProductInfoLoaded);

      // Watch for new [data-size-drawer-trigger] elements appearing in the DOM
      // (e.g. after a product-info viewTransition swap). The product-info:loaded
      // event covers connectedCallback-based swaps; the observer covers any
      // DOM update that simply replaces innerHTML without re-connecting the
      // <product-info> element.
      this._mutationObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('[data-size-drawer-trigger]')) {
              this._syncAllTriggerLabels();
              return;
            }
            if (node.querySelector && node.querySelector('[data-size-drawer-trigger]')) {
              this._syncAllTriggerLabels();
              return;
            }
          }
        }
      });
      this._mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('product-info:loaded', this._onProductInfoLoaded);
      if (this.dialog) {
        this.dialog.removeEventListener('click', this._onDialogClick);
        this.dialog.removeEventListener('close', this._onDialogClose);
      }
      if (this._mutationObserver) {
        this._mutationObserver.disconnect();
      }
    }

    _onDocClick(event) {
      const trigger = event.target.closest('[data-size-drawer-trigger]');
      if (trigger) {
        event.preventDefault();
        this.open(trigger);
        return;
      }
      const closer = event.target.closest('[data-size-drawer-close]');
      if (closer && this.dialog.open) {
        event.preventDefault();
        this.close();
      }
    }

    _onDialogClick(event) {
      // Guide link click → open ring sizer
      if (event.target.closest('[data-size-drawer-guide]')) {
        this.openGuide();
        return;
      }
      // Click on backdrop (outside the inner box) → close
      if (event.target === this.dialog) {
        this.close();
      }
    }

    _onDialogClose() {
      this.dialog.dataset.state = 'closed';
      if (this.currentTrigger) {
        this.currentTrigger.setAttribute('aria-expanded', 'false');
        this.currentTrigger.focus({ preventScroll: true });
      }
    }

    _onProductInfoLoaded() {
      this._syncAllTriggerLabels();
    }

    _syncAllTriggerLabels() {
      document.querySelectorAll('[data-size-drawer-trigger]').forEach((trigger) => {
        const sourceId = trigger.id;
        const select = document.querySelector('[data-size-trigger-source="' + sourceId + '"]');
        if (!select) return;
        const selectEl = trigger.querySelector('.select');
        if (!selectEl) return;
        const selectedOpt = select.options[select.selectedIndex];
        const label = selectedOpt ? (selectedOpt.textContent || selectedOpt.value).trim() : '';
        selectEl.setAttribute('data-selected-label', label);
      });
    }

    _onListClick(event) {
      const btn = event.target.closest('.size-drawer__option-btn');
      if (!btn) return;
      if (btn.getAttribute('aria-disabled') === 'true') return;
      if (!this.currentSelect) return;

      const value = btn.dataset.value;
      this.currentSelect.value = value;
      this.currentSelect.dispatchEvent(new Event('change', { bubbles: true }));

      // Aggiorna il label del trigger PRIMA che il <select> venga ripopolato
      // dal variant swap (utile per cluster-versioni che cambiano URL)
      this._syncTriggerLabel(this.currentSelect);

      this.close();

      // Safety net: re-sync after async product-info swap completes
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._syncAllTriggerLabels());
      });
    }

    openGuide() {
      const ringSizer = document.querySelector('cz-ring-sizer');
      if (!ringSizer) {
        console.warn('cz-ring-sizer non trovato — verifica che ring-sizer-drawer sia renderizzato');
        return;
      }
      const select = this.currentSelect;
      if (!select) return;
      const sizes = Array.from(select.querySelectorAll('option'))
        .filter((o) => o.value && o.value.trim())
        .map((o) => ({
          value: o.value.trim(),
          available: o.dataset.optionUnavailable !== 'true',
        }));
      const values = sizes.map((s) => s.value);

      // Detect format inline (mirror of scripts/ring-sizer/src/size-mapping.js detectFormat)
      const detectFormat = (s) => {
        if (!s.length) return 'eu';
        if (s.some((x) => /\(\d+\)/.test(x))) return 'dual';
        // dual col trattino: "14 - 54", "56 - 16" (due numeri separati da -)
        if (s.some((x) => /\d+(?:[.,]\d+)?\s*-\s*\d+(?:[.,]\d+)?/.test(x))) return 'dual';
        const nums = s.map((x) => parseFloat(x)).filter((n) => !isNaN(n));
        if (nums.every((n) => n >= 40 && n <= 70)) return 'eu';
        if (nums.every((n) => n >= 1 && n <= 30)) return 'it';
        return 'eu';
      };

      const format = detectFormat(values);

      // Capture select reference because this.currentSelect resets on dialog close
      const targetSelect = select;
      const syncAll = () => this._syncAllTriggerLabels();
      this.close();
      setTimeout(() => {
        ringSizer.open({
          availableSizes: sizes,
          format,
          onSelect: (value) => {
            targetSelect.value = value;
            targetSelect.dispatchEvent(new Event('change', { bubbles: true }));
            syncAll();
            requestAnimationFrame(() => requestAnimationFrame(syncAll));
          },
        });
      }, 280);
    }

    _focusInitial() {
      const selected = this.listEl.querySelector('[aria-selected="true"]:not([aria-disabled="true"])');
      const fallback = this.listEl.querySelector('.size-drawer__option-btn:not([aria-disabled="true"])');
      const target = selected || fallback;
      if (target) target.focus();
    }

    _onListKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const buttons = Array.from(
        this.listEl.querySelectorAll('.size-drawer__option-btn:not([aria-disabled="true"])')
      );
      const currentIdx = buttons.indexOf(document.activeElement);
      let nextIdx;
      if (event.key === 'ArrowDown') {
        nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % buttons.length;
      } else {
        nextIdx = currentIdx <= 0 ? buttons.length - 1 : currentIdx - 1;
      }
      if (buttons[nextIdx]) buttons[nextIdx].focus();
    }

    open(trigger) {
      const sourceId = trigger.id;
      const select = document.querySelector('[data-size-trigger-source="' + sourceId + '"]');
      if (!select) return;

      this.currentTrigger = trigger;
      this.currentSelect = select;

      // Il link "Non conosci la tua misura?" ha senso solo dove il wizard
      // esiste (anelli multi-taglia) — il drawer è un guscio globale.
      const guideLink = this.querySelector('[data-size-drawer-guide]');
      if (guideLink) guideLink.hidden = trigger.dataset.sizerEnabled !== 'true';

      this._renderList(select);

      trigger.setAttribute('aria-expanded', 'true');
      if (!this.dialog.open) this.dialog.showModal();
      this.dialog.dataset.state = 'open';
      // Focus deferito al prossimo frame: showModal() è sincrono ma il browser
      // applica focus default al dialog. Lo sovrascriviamo al frame successivo.
      requestAnimationFrame(() => this._focusInitial());
    }

    _renderList(select) {
      const options = Array.from(select.options);
      this.listEl.innerHTML = '';

      options.forEach((opt) => {
        const li = document.createElement('li');
        li.className = 'size-drawer__option';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'size-drawer__option-btn';
        btn.setAttribute('role', 'option');
        btn.dataset.value = opt.value;

        const isSelected = opt.selected;
        const isDisabled = opt.dataset.optionUnavailable === 'true';

        btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        if (isDisabled) {
          btn.setAttribute('aria-disabled', 'true');
          btn.tabIndex = -1;
        }

        btn.textContent = isDisabled ? opt.value : (opt.textContent || opt.value).trim();

        li.appendChild(btn);
        this.listEl.appendChild(li);
      });

      this._syncTriggerLabel(select);
    }

    _syncTriggerLabel(select) {
      if (!this.currentTrigger) return;
      const selectEl = this.currentTrigger.querySelector('.select');
      if (!selectEl) return;
      const selectedOpt = select.options[select.selectedIndex];
      const label = selectedOpt ? (selectedOpt.textContent || selectedOpt.value).trim() : '';
      selectEl.setAttribute('data-selected-label', label);
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }
  }

  customElements.define('cz-size-drawer', CzSizeDrawer);
})();
